import type { CSSProperties } from "react";

type Variant = "roof" | "sun" | "panel" | "house" | "shingles";
type Side = "left" | "right";
type Tone = "primary" | "white";

interface SectionDecorProps {
  variant?: Variant;
  side?: Side;
  tone?: Tone;
  /** 0-1, defaults ~0.06 */
  opacity?: number;
  /** width in px on desktop */
  size?: number;
  className?: string;
}

/**
 * Oversized minimal blueprint-style line-art decoration.
 * - Absolutely positioned, pointer-events: none, aria-hidden.
 * - Sits behind content (z-0). Ensure parent has `relative overflow-hidden`
 *   and content lives in a wrapper with `relative z-10`.
 * - Hidden on mobile, reduced on tablet, full size on desktop.
 */
export function SectionDecor({
  variant = "roof",
  side = "right",
  tone = "primary",
  opacity = 0.06,
  size = 720,
  className = "",
}: SectionDecorProps) {
  const toneClass = tone === "white" ? "text-white" : "text-primary";
  const style: CSSProperties = {
    opacity,
    width: size,
    height: size,
    [side]: `-${Math.round(size * 0.18)}px`,
  } as CSSProperties;

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute top-1/2 -translate-y-1/2 z-0 hidden md:block ${toneClass} ${className}`}
      style={style}
    >
      <svg
        viewBox="0 0 400 400"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.25}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-full w-full"
      >
        {variant === "roof" && <RoofArt />}
        {variant === "sun" && <SunArt />}
        {variant === "panel" && <PanelArt />}
        {variant === "house" && <HouseArt />}
        {variant === "shingles" && <ShinglesArt />}
      </svg>
    </div>
  );
}

function RoofArt() {
  return (
    <g>
      {/* Roof outline */}
      <path d="M40 220 L200 90 L360 220" />
      <path d="M70 220 L200 120 L330 220" />
      {/* Ridge lines */}
      <path d="M200 90 L200 220" />
      {/* Shingle rows */}
      <path d="M90 190 L310 190" strokeDasharray="6 8" />
      <path d="M110 170 L290 170" strokeDasharray="6 8" />
      <path d="M130 150 L270 150" strokeDasharray="6 8" />
      {/* Chimney */}
      <rect x="250" y="130" width="26" height="52" />
      {/* House body */}
      <path d="M40 220 L40 340 L360 340 L360 220" />
      <rect x="170" y="260" width="60" height="80" />
      <rect x="80" y="250" width="50" height="40" />
      <rect x="270" y="250" width="50" height="40" />
      {/* Dimension marks */}
      <path d="M20 350 L380 350" />
      <path d="M20 345 L20 355 M380 345 L380 355" />
    </g>
  );
}

function SunArt() {
  return (
    <g>
      <circle cx="200" cy="200" r="70" />
      <circle cx="200" cy="200" r="95" strokeDasharray="4 10" />
      {[...Array(16)].map((_, i) => {
        const a = (i / 16) * Math.PI * 2;
        const x1 = 200 + Math.cos(a) * 115;
        const y1 = 200 + Math.sin(a) * 115;
        const x2 = 200 + Math.cos(a) * 160;
        const y2 = 200 + Math.sin(a) * 160;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
      })}
      <circle cx="200" cy="200" r="180" strokeDasharray="2 12" />
    </g>
  );
}

function PanelArt() {
  return (
    <g>
      {/* Solar panel grid */}
      <path d="M60 140 L340 100 L360 260 L80 300 Z" />
      {[...Array(5)].map((_, r) => (
        <path
          key={`r${r}`}
          d={`M${64 + r * 4} ${146 + r * 32} L${344 + r * 4} ${106 + r * 32}`}
        />
      ))}
      {[...Array(6)].map((_, c) => {
        const t = c / 6;
        const x1 = 60 + (340 - 60) * t;
        const y1 = 140 + (100 - 140) * t;
        const x2 = 80 + (360 - 80) * t;
        const y2 = 300 + (260 - 300) * t;
        return <line key={`c${c}`} x1={x1} y1={y1} x2={x2} y2={y2} />;
      })}
      {/* Stand */}
      <path d="M140 260 L160 340" />
      <path d="M280 240 L300 340" />
    </g>
  );
}

function HouseArt() {
  return (
    <g>
      <path d="M60 210 L200 90 L340 210" />
      <path d="M90 210 L90 340 L310 340 L310 210" />
      <rect x="175" y="250" width="55" height="90" />
      <rect x="115" y="240" width="45" height="45" />
      <rect x="245" y="240" width="45" height="45" />
      <path d="M115 262 L160 262 M137 240 L137 285" />
      <path d="M245 262 L290 262 M267 240 L267 285" />
      {/* Sun accent */}
      <circle cx="330" cy="70" r="26" />
      <path d="M330 30 L330 20 M330 120 L330 110 M280 70 L270 70 M390 70 L380 70" />
    </g>
  );
}

function ShinglesArt() {
  return (
    <g>
      {[...Array(10)].map((_, r) =>
        [...Array(12)].map((_, c) => {
          const offset = r % 2 === 0 ? 0 : 18;
          return (
            <path
              key={`${r}-${c}`}
              d={`M${20 + c * 36 + offset} ${40 + r * 34} h34 v22 h-34 z`}
            />
          );
        })
      )}
    </g>
  );
}
